import * as React from 'react';
import type { GsocIamRoleFragment } from '../code-generated/graphqlOperations.generated';
import { useGsocListIamRolesQuery } from '../code-generated/graphqlOperations.generated';
import { NOT_LOADED, PermissionsContext } from './Permissions';

const TIMEOUT_BUFFER_SECONDS = 30;

/**
 * Provides permissions context to child components by fetching IAM roles and managing
 * temporary credentials with automatic refresh functionality.
 *
 * This provider handles:
 * - Fetching IAM roles via GraphQL query
 * - Managing CAZ (Customer Authorization) justification state
 * - Automatic credential refresh before expiration
 * - Loading states and error handling
 *
 * @param props - Component props
 * @param props.children - Child components that will receive permissions context
 *
 * @example
 * ```tsx
 * // Basic usage in application root
 * <PermissionsProvider>
 *   <App />
 * </PermissionsProvider>
 * ```
 *
 * @example
 * ```tsx
 * // Usage in tests with mock GraphQL provider
 * <MockedProvider mocks={mockRoles}>
 *   <PermissionsProvider>
 *     <ComponentUnderTest />
 *   </PermissionsProvider>
 * </MockedProvider>
 * ```
 *
 * ## TESTING GUIDANCE
 *
 * **SHOULD be used directly in tests rather than mocked** to ensure proper integration
 * testing of permission-dependent components. This provider contains essential business
 * logic for credential management and loading states that should be tested.
 *
 * ### Recommended Test Setup:
 * - Use Apollo MockedProvider to mock the underlying GraphQL query
 * - Test loading states by providing empty mocks initially
 * - Test permission states by providing different role configurations
 * - Test credential refresh behavior with time-based mocks
 *
 * ### What to Mock Instead:
 * - Mock `useGsocListIamRolesQuery` at the GraphQL level using MockedProvider
 * - Mock `window.setTimeout` and `Date.now()` for time-dependent tests
 * - Mock network requests rather than this provider component
 *
 * @see {@link PermissionsContext} for the context interface
 * @see {@link useDoesUserHavePermissionClaims} for consuming permissions
 */
export function PermissionsProvider(props: { readonly children: React.ReactNode }) {
  const [cazJustification, setCazJustification] = React.useState<string | null>(null);
  const { data, refetch, loading } = useGsocListIamRolesQuery({
    variables: { cazJustification },
  });

  /** Refetch AwsTemporaryCredentials just before they expire */
  React.useEffect(() => {
    if (data?.gsocListIamRoles != null) {
      const minimumExpiration = data.gsocListIamRoles.roles.reduce((min, role) => {
        const expiration = role.awsTemporaryCredentials?.expiration.getTime();
        if (expiration !== undefined) {
          return (min === 0 && expiration > 0) || expiration < min ? expiration : min;
        }
        return min;
      }, 0);
      const timeoutDuration = ((minimumExpiration - Date.now()) / 1000 - TIMEOUT_BUFFER_SECONDS) * 1000;
      // eslint-disable-next-line @typescript-eslint/no-misused-promises
      let timeoutHandle: number | null = window.setTimeout(refetch, timeoutDuration);

      return () => {
        if (timeoutHandle !== null) {
          window.clearTimeout(timeoutHandle);
          timeoutHandle = null;
        }
      };
    }
    return () => {
      /** Intentionally Empty */
    };
  }, [data?.gsocListIamRoles, refetch]);

  /** Clear cazJustification after 1 hour */
  React.useEffect(() => {
    if (cazJustification !== null) {
      const timeoutHandle = window.setTimeout(() => setCazJustification(null), 60 * 60 * 1000);
      return () => {
        window.clearTimeout(timeoutHandle);
      };
    }
    return () => {
      /** Intentionally Empty */
    };
  }, [cazJustification]);

  const providedValue = React.useMemo<{
    roles: readonly GsocIamRoleFragment[] | typeof NOT_LOADED;
    setCazJustification: ((cazJustification: string | null) => void) | null;
    cazJustification: string | null;
  }>(
    () => ({
      roles: loading ? NOT_LOADED : (data?.gsocListIamRoles?.roles ?? []),
      setCazJustification,
      cazJustification,
    }),
    [loading, data?.gsocListIamRoles?.roles, cazJustification, setCazJustification],
  );

  return <PermissionsContext.Provider value={providedValue}>{props.children}</PermissionsContext.Provider>;
}
